import React, { FC, ReactNode } from 'react'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { Container } from 'components/layout'
import { IPrimaryActionProps, ResponsivePrimaryAction } from './ResponsivePrimaryAction'

type TClassKey = 'content' | 'footerWrapper' | 'footer'

const useResponsiveLayoutStyles = makeStyles(theme =>
  createStyles<TClassKey, { isFooterVisible?: boolean }>({
    content: {
      height: 'auto',
      [theme.breakpoints.up('md')]: {
        height: isFooterVisible => `calc(100% - ${isFooterVisible ? theme.spacing(11) : '0px'})`,
      },
    },
    footerWrapper: {
      paddingBottom: isFooterVisible => (isFooterVisible ? theme.spacing(11) : 0),
    },
    footer: {
      background: brandColors.white,
      padding: theme.spacing(3),
      boxShadow: theme.shadows[10],
      position: 'fixed',
      bottom: 0,
      width: '100%',
    },
  })
)

export interface IResponsiveLayoutProps {
  /**
   * The Primary Action Button options, for desktop and mobile view
   */
  primaryActionProps?: IPrimaryActionProps
  /**
   * Title to be shown on the top of the page
   */
  title: string
  /**
   * Content of the page
   */
  children: ReactNode | ReactNode[]
  /**
   * Add back button to top nav
   * @default false
   */
  showBackButton?: boolean
  /**
   * A function to be executed when the back button is clicked
   * If the function is not provided the back button simply goes back in the browser history
   */
  onPressBackButton?: () => void
  /**
   * Content of the footer - that will be shown at the bottom of the page as
   * fixed position
   * > This prop is mobile exclusive
   */
  footer?: ReactNode
  /**
   * Remove the default padding top of the page
   * @default false
   */
  hidePaddingTop?: boolean
  /**
   * Add a classname to the container
   * @default undefined
   */
  containerClassName?: string
}

export const ResponsiveLayout: FC<IResponsiveLayoutProps> = ({
  children,
  containerClassName,
  footer,
  hidePaddingTop = false,
  onPressBackButton,
  primaryActionProps,
  showBackButton = false,
  title,
}) => {
  const { isMobile } = useBreakpointContext()
  const isFooterVisible = isMobile && !!footer
  const classes = useResponsiveLayoutStyles({ isFooterVisible })

  // show the menu ONLY IF a back button callback IS NOT provided
  const showMenu = isMobile && !onPressBackButton

  return (
    <>
      <TopNav
        title={title}
        onPressBackButton={onPressBackButton}
        showMenu={showMenu}
        showBackButton={showBackButton}>
        {primaryActionProps && <ResponsivePrimaryAction {...primaryActionProps} />}
      </TopNav>

      <Container fullHeight={isMobile} className={containerClassName} hidePadding>
        <Box className={classes.content} pt={hidePaddingTop ? 0 : 3} px={0} pb={isMobile ? 1 : 7}>
          {children}
        </Box>

        {isFooterVisible && (
          <Box className={classes.footerWrapper}>
            <Box className={classes.footer}>{footer}</Box>
          </Box>
        )}
      </Container>
    </>
  )
}
